import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import {reporter} from 'io-ts-reporters';
import {isString} from 'lodash';

import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';

/*
 * Constants.
 */

/** Diffable boot resource property names. */
// Corresponds to https://github.com/frontapp/front/blob/c3d8c38/server/routes/boot_routes.js#L401
const rawBootDeltaKeys = ['canned_answers', 'signatures', 'team', 'inboxes', 'channels'] as const;
const rawBootDeltaKeysSet = new Set<string>(rawBootDeltaKeys);

/** For each diffable boot resource, a function that determines whether a resource is a tombstone. */
const rawBootDeltaTombstonePredicates: RawBootDeltaMap<RawBootDeltaResourcePredicate> = {
  // Teammates don't have tombstones, and we want to keep them regardless of their deleted_at.
  team: () => false,
  // These resources do have tombstones.
  canned_answers: isResourceDeleted,
  signatures: isResourceDeleted,
  inboxes: isResourceDeleted,
  channels: isResourceDeleted,
};

/*
 * Runtime types.
 */

/** Diffable boot resource property name. */
const RRawBootDeltaKey = new t.Type<RawBootDeltaKey, string>(
  'RawBootDeltaKey',
  isRawBootDeltaKey,
  (value, context) => (isRawBootDeltaKey(value) ? t.success(value) : t.failure(value, context)),
  (value) => value,
);

/** Diffable boot resource. */
const RRawBootDeltaResource = t.intersection([
  t.type({
    id: t.number,
  }),
  t.partial({
    deleted_at: t.union([t.number, t.undefined]),
  }),
]);

/** Part of the raw boot that contains company/user information. */
const RRawBootUserInfo = t.type({
  user: t.type({
    id: t.number,
  }),
  company: t.type({
    id: t.number,
  }),
});

// It's easier to define this object by hand and let TypeScript complain if it needs to be updated.
const rawBootDeltaProps: t.Props & RawBootDeltaMap<any> = {
  canned_answers: t.readonlyArray(RRawBootDeltaResource),
  signatures: t.readonlyArray(RRawBootDeltaResource),
  team: t.readonlyArray(RRawBootDeltaResource),
  inboxes: t.readonlyArray(RRawBootDeltaResource),
  channels: t.readonlyArray(RRawBootDeltaResource),
};

/** Part of the raw boot that full and delta boots have in common. */
const RRawBootBase = t.intersection([
  RRawBootUserInfo,
  t.type({
    ts: t.number,
    launchdarkly: t.type({
      flags: t.union([t.dictionary(t.string, t.unknown), t.undefined]),
    }),
    ...rawBootDeltaProps,
  }),
]);

/** Normal/full boot. */
const RRawBoot = t.intersection(
  [
    RRawBootBase,
    t.partial({
      /** Client-only property to remember the last time we fetched a full/non-diff boot from the server. */
      client_original_ts: t.number,
      // There MUST NOT be a deltas array in a normal boot.
      deltas: t.undefined,
    }),
  ],
  'RawBoot',
);

/** Delta boot. */
const RRawBootDelta = t.intersection(
  [
    RRawBootBase,
    t.type({
      // There MUST be a deltas array in a delta boot, and it must have known diffable resource property names only.
      deltas: t.readonlyArray(RRawBootDeltaKey),
    }),
  ],
  'RawBootDelta',
);

/*
 * Types.
 */

type RawBootDeltaKey = typeof rawBootDeltaKeys[any];
type RawBootDeltaResource = t.TypeOf<typeof RRawBootDeltaResource>;
type RawBootDeltaResourcePredicate = (resource: RawBootDeltaResource) => boolean;
type RawBootDeltaMap<V> = {[key in RawBootDeltaKey]: V};

export type RawBootUserInfo = t.TypeOf<typeof RRawBootUserInfo>;
export type RawBoot = t.TypeOf<typeof RRawBoot>;
export type RawBootDelta = t.TypeOf<typeof RRawBootDelta>;

/*
 * Type guards.
 */

function isRawBootDeltaKey(value: unknown): value is RawBootDeltaKey {
  return isString(value) && rawBootDeltaKeysSet.has(value);
}

export const isRawBootUserInfo = RRawBootUserInfo.is.bind(RRawBootUserInfo);
export const isRawBoot = RRawBoot.is.bind(RRawBoot);
export const isRawBootDelta = RRawBootDelta.is.bind(RRawBootDelta);

/*
 * Helpers.
 */

export function decodeRawBoot(input: unknown): RawBoot {
  return decode(RRawBoot, input);
}

export function decodeRawBootDelta(input: unknown): RawBootDelta {
  return decode(RRawBootDelta, input);
}

function decode<A>(decoder: t.Decoder<unknown, A>, input: unknown): A {
  const errorsOrValue = decoder.decode(input);

  if (!E.isRight(errorsOrValue)) {
    const errors = reporter(errorsOrValue);
    throw new ErrorWithData(`Failed to decode ${decoder.name}`, {errors});
  }

  return errorsOrValue.right;
}

function canPatchRawBoot(rawBoot: RawBoot, rawBootDelta: RawBootDelta): boolean {
  return (
    rawBoot.ts <= rawBootDelta.ts &&
    rawBoot.company.id === rawBootDelta.company.id &&
    rawBoot.user.id === rawBootDelta.user.id
  );
}

export function patchRawBoot(rawBoot: RawBoot, rawBootDelta: RawBootDelta): RawBoot | undefined {
  if (!canPatchRawBoot(rawBoot, rawBootDelta)) {
    return undefined;
  }

  const patchedRawBoot = {
    ...rawBootDelta,
    client_original_ts: rawBoot.client_original_ts ?? rawBoot.ts,
    deltas: undefined,
  };

  const deltaKeys = rawBootDelta.deltas || [];
  deltaKeys.forEach((deltaKey) => {
    patchedRawBoot[deltaKey] = patchRawBootResources(
      rawBoot[deltaKey],
      rawBootDelta[deltaKey],
      rawBootDeltaTombstonePredicates[deltaKey],
    );
  });

  return patchedRawBoot;
}

function patchRawBootResources(
  resources: ReadonlyArray<RawBootDeltaResource>,
  deltaResources: ReadonlyArray<RawBootDeltaResource>,
  isTombstone: (deltaResource: RawBootDeltaResource) => boolean,
): ReadonlyArray<RawBootDeltaResource> {
  const deltaIds = new Set(deltaResources.map((resource) => resource.id));

  return [
    // Remove resources that appear in the patch.
    ...resources.filter((resource) => !deltaIds.has(resource.id)),
    // Add updated resources.
    ...deltaResources.filter((resource) => !isTombstone(resource)),
  ];
}

// Corresponds to https://github.com/frontapp/front/blob/c3d8c38/server/resources/base/change_tracking.ts#L651
function isResourceDeleted(resource: RawBootDeltaResource): boolean {
  return Boolean(resource.deleted_at && resource.deleted_at > 0);
}
